<script setup lang="ts">
    import type { ButtonMenuProps } from '@/types/components/button';
    import { PhCaretDown } from '@phosphor-icons/vue';
    import { ref, computed } from 'vue';
    import { useRoute } from 'vue-router';

    const props = defineProps<ButtonMenuProps>();
    const emit = defineEmits(['toggle']);

    const route = useRoute();
    const openDropdown = ref(false);

    const hasChildren = computed(() => {
        return Array.isArray(props.children) && props.children.length > 0;
    });

    const isDropdownOpen = computed(() => {
        return props.isOpen !== undefined ? props.isOpen : openDropdown.value;
    });

    const active = (item: unknown) => item === route.meta.group;

    const handleDropdown = () => {
        if (props.children) {
            if (props.isOpen !== undefined) {
                emit('toggle');
            } else {
                openDropdown.value = !openDropdown.value;
            }
        }
    };
</script>

<template>
    <div>
        <router-link
            v-if="!hasChildren"
            :to="path"
            :class="[
                active(group) ? 'bg-brand-50 text-brand-500' : 'text-neutral-800',
                'relative py-2.5 pl-6 pr-[13px] w-full block text-left transition-colors duration-200 hover:bg-brand-100'
            ]"
        >
            <div class="flex justify-between items-center">
                <div class="flex gap-6">
                    <div class="flex items-center">
                        <component
                            :is="icon"
                            :size="24"
                            class="text-2xl mr-3 shrink-0"
                            :weight="active(group) ? 'fill' : 'regular'"
                        />
                        <b class="text-sm">{{ label }}</b>
                    </div>
                </div>
                <!-- <div
                    v-if="group == 'notification' && notificationStore.count > 0"
                    class="flex items-center justify-center notif-count w-[24px] h-[24px] text-xs bg-[#B0BAEC] rounded-full"
                >
                    {{
                        notificationStore.count > 9
                            ? '9+'
                            : notificationStore.count
                              ? notificationStore.count
                              : ''
                    }}
                </div> -->
            </div>
            <div
                v-if="active(group)"
                class="absolute right-0 top-[9%] bottom-[9%] w-1 bg-brand-500 rounded-l-2xl"
            ></div>
        </router-link>

        <button
            v-else
            @click="handleDropdown"
            :class="[
                active(group) ? 'bg-brand-50 text-brand-500' : 'text-neutral-800',
                'shrink-0 relative py-2.5 pl-6 pr-[13px] w-full text-left transition-colors duration-200 hover:bg-brand-100'
            ]"
        >
            <div class="flex justify-between items-center">
                <div class="flex items-center">
                    <component
                        :is="icon"
                        :size="24"
                        class="text-2xl mr-3 shrink-0"
                        :weight="active(group) ? 'fill' : 'regular'"
                    />
                    <span class="font-semibold text-sm text-nowrap">{{ label }}</span>
                </div>
                <component
                    v-if="hasChildren"
                    :is="PhCaretDown"
                    size="24"
                    :class="
                        isDropdownOpen
                            ? 'rotate-180 transition-transform duration-200'
                            : 'transition-transform duration-200'
                    "
                />
            </div>
            <div
                v-if="active(group)"
                class="absolute right-0 top-[9%] bottom-[9%] w-1 bg-brand-500 rounded-l-2xl"
            ></div>
        </button>

        <!-- Submenu dengan Transisi -->
        <Transition
            enter-active-class="transition-all duration-100 ease-out"
            leave-active-class="transition-all duration-100 ease-in"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-96"
            leave-from-class="opacity-100 max-h-96"
            leave-to-class="opacity-0 max-h-0"
        >
            <div v-if="hasChildren && isDropdownOpen" class="flex flex-col overflow-hidden">
                <router-link
                    v-for="child in children as Array<{ path: string; label: string }>"
                    :key="child.path"
                    :to="child.path"
                    class="py-3 pr-6 pl-14 block text-left transition-colors duration-200 hover:bg-brand-100"
                    :class="
                        route.path.startsWith(child.path)
                            ? 'bg-brand-50 text-brand-500 font-semibold'
                            : 'text-neutral-800'
                    "
                >
                    {{ child.label }}
                </router-link>
            </div>
        </Transition>
    </div>
</template>
